import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import authApi from "../api/authApi";
import useIdleTimer from "../hooks/useIdleTimer";
import { setToken, getToken, clearToken } from "../utils/tokenManager";
import { getDefaultRouteForUser } from "../utils/routeUtils";

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

/**
 * AuthProvider: Provee autenticación, permisos y control de sesión.
 * Maneja:
 *  - access token en memoria (tokenManager) (ACCESS_TTL)
 *  - refresh token (HttpOnly)
 *  - timeout de inactividad (IDLE_TIMEOUT)
 *  - permisos jerárquicos
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(() => getToken());
  const [sedes, setSedes] = useState([]);
  const [sedeActiva, setSedeActiva] = useState(() => {
    try {
      const saved = sessionStorage.getItem('sedeActiva');
      if (saved && saved !== "undefined" && saved !== "null") {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error("[AuthContext] Error parsing sedeActiva from sessionStorage:", e);
    }
    return null;
  });

  // Configurables desde .env (fallback a 2700s = 45 min para evitar NaN en timers)
  const ACCESS_TTL = parseInt(process.env.REACT_APP_ACCESS_TTL, 10) || 2700; // segundos
  const IDLE_TIMEOUT = parseInt(process.env.REACT_APP_IDLE_TIMEOUT, 10) || 2700; // segundos

  const applyUserData = useCallback((data, isFreshLogin = false) => {
    if (!data) return;
    setUser({
      id: data.id_usuario,
      nombre: data.nombre,
      rol: data.rol_id,
      es_supersu: Boolean(data.es_supersu)
    });
    setPermissions(data.permisos || {});
    
    const sedesList = [...(data.sedes || [])].sort((a, b) => {
      const ordenA = a.orden != null ? Number(a.orden) : Infinity;
      const ordenB = b.orden != null ? Number(b.orden) : Infinity;
      if (ordenA !== ordenB) return ordenA - ordenB;
      return (a.nombreSede || "").localeCompare(b.nombreSede || "");
    });
    setSedes(sedesList);

    // 🔹 Lógica de selección de sede activa
    try {
      const savedSede = isFreshLogin ? null : sessionStorage.getItem('sedeActiva');
      const hasValidSavedSede = savedSede && savedSede !== "undefined" && savedSede !== "null";

      if (hasValidSavedSede) {
        const parsed = JSON.parse(savedSede);
        const existsInUserSedes = sedesList.some(s => String(s.id) === String(parsed?.id));
        if (existsInUserSedes) {
          setSedeActiva(parsed);
          return;
        }
      }

      if (sedesList.length === 1) {
        const uniqueSede = sedesList[0];
        setSedeActiva(uniqueSede);
        sessionStorage.setItem('sedeActiva', JSON.stringify(uniqueSede));
      } else {
        // Si hay múltiples sedes o ninguna guardada, forzamos selección explícita
        setSedeActiva(null);
        sessionStorage.removeItem('sedeActiva');
      }
    } catch (e) {
      setSedeActiva(null);
    }
  }, []);

  // 🔹 Refresca el access token usando refresh token HttpOnly
  const performRefresh = useCallback(async () => {
    try {
      const result = await authApi.refresh(); // devuelve { token, user }
      if (!result || !result.token) return null;

      setAccessToken(result.token);
      setToken(result.token);
      if (result?.user) {
        applyUserData(result.user, false);
      }
      return result;
    } catch {
      return null;
    }
  }, [applyUserData]);

  // 🔹 Inicialización: intenta usar access token o refresh token
  useEffect(() => {
    const init = async () => {
      // Si la navegación actual está en el portal del paciente, no ejecutar la lógica admin ni limpiar tokens
      if (typeof window !== "undefined" && window.location.pathname.startsWith("/portal-paciente")) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let token = accessToken;
        const hasSession = sessionStorage.getItem("hasSession") === "true";

        if (!token && hasSession) {
          const result = await performRefresh();
          token = result?.token;
          if (result?.user) applyUserData(result.user, false);
        }

        if (!token || !hasSession) {
          setUser(null);
          setPermissions({});
          clearToken();
        }
      } catch {
        setUser(null);
        setPermissions({});
        clearToken();
        sessionStorage.removeItem("hasSession");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [accessToken, performRefresh, applyUserData]);

  // 🔹 Login
  const login = async (email, password) => {
    setLoading(true);
    try {
      // Limpiar rastro de sesión y sede anterior para evitar filtración entre usuarios
      sessionStorage.removeItem("sedeActiva");
      sessionStorage.removeItem("hasSession");

      const result = await authApi.login(email, password);
      setAccessToken(result.token);
      setToken(result.token);
      sessionStorage.setItem("hasSession", "true"); 

      applyUserData(result.user, true);

      const sedesList = [...(result.user?.sedes || [])].sort((a, b) => {
        const ordenA = a.orden != null ? Number(a.orden) : Infinity;
        const ordenB = b.orden != null ? Number(b.orden) : Infinity;
        if (ordenA !== ordenB) return ordenA - ordenB;
        return (a.nombreSede || "").localeCompare(b.nombreSede || "");
      });

      const userObj = {
        id: result.user?.id_usuario,
        nombre: result.user?.nombre,
        rol: result.user?.rol_id,
        es_supersu: Boolean(result.user?.es_supersu)
      };
      const permsObj = result.user?.permisos || {};
      const check = (perm) => hasPermission(perm, userObj, permsObj);
      const defaultRoute = getDefaultRouteForUser(check, userObj);

      return {
        sedes: sedesList,
        defaultRoute
      };

    } catch (err) {
      setUser(null);
      setPermissions({});
      setAccessToken(null);
      setSedes([]);
      setSedeActiva(null);
      clearToken();
      sessionStorage.removeItem("hasSession");
      sessionStorage.removeItem("sedeActiva");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Logout
  const logout = async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setPermissions({});
      setAccessToken(null);
      setSedes([]);
      setSedeActiva(null);
      clearToken();
      sessionStorage.removeItem("hasSession");
      sessionStorage.removeItem("sedeActiva");
      window.location.href = "/login";
    }
  };

  // 🔹 Verifica permisos jerárquicos
  // const hasPermission = (pathArray) => {
  //   if (!user || !permissions || Object.keys(permissions).length === 0) return false;
  //   let ref = permissions;
  //   for (let nivel of pathArray) {
  //     if (!ref[nivel]) return false;
  //     ref = ref[nivel];
  //   }
  //   return ref === true || ref._enabled === true;
  // };

  const hasPermission = useCallback((pathArray, customUser = null, customPerms = null) => {
    const targetUser = customUser !== null ? customUser : user;
    const targetPerms = customPerms !== null ? customPerms : permissions;

    // 👑 BYPASS SUPER-SU / ROL 1: Acceso irrestricto a todos los módulos y acciones
    if (targetUser?.es_supersu || String(targetUser?.rol) === "1") {
      return true;
    }

    if (!targetUser || !targetPerms || !Array.isArray(pathArray)) return false;

    let ref = targetPerms;

    for (let nivel of pathArray) {
      if (!ref || !(nivel in ref)) return false;
      ref = ref[nivel];
    }

    // Caso 1: permiso booleano final
    if (typeof ref === "boolean") return ref;

    // Caso 2: si tiene _enabled
    if (typeof ref === "object" && "_enabled" in ref) {
      if (ref._enabled === true) return true;
    }

    // Caso 3: si algún hijo es true
    if (typeof ref === "object") {
      return Object.values(ref).some(v => v === true);
    }

    return false;
  }, [user, permissions]);

  const getDefaultRoute = useCallback((customUser = null, customPerms = null) => {
    const targetUser = customUser !== null ? customUser : user;
    const check = (perm) => hasPermission(perm, targetUser, customPerms);
    return getDefaultRouteForUser(check, targetUser);
  }, [hasPermission, user]);

  // 🔹 Idle timer: cierra sesión si no hay actividad
  useIdleTimer(() => {
    if (user) logout();
  }, IDLE_TIMEOUT ? IDLE_TIMEOUT * 1000 : undefined);

  // 🔹 Auto-refresh: antes de que expire el access token
  useEffect(() => {
    if (!accessToken || !ACCESS_TTL || ACCESS_TTL <= 0) return;

    const interval = setInterval(() => {
      performRefresh();
    }, (ACCESS_TTL / 2) * 1000);

    return () => clearInterval(interval);
  }, [accessToken, ACCESS_TTL, performRefresh]);

  // Función para seleccionar sede activa y guardarla en sesión
  const selectSede = (sede) => {
    setSedeActiva(sede);
    sessionStorage.setItem('sedeActiva', JSON.stringify(sede));
  };

  return (
    <AuthContext.Provider value={{ user, permissions, login, logout, hasPermission, getDefaultRoute, loading, accessToken, sedes, sedeActiva, selectSede }} >
      {children}
    </AuthContext.Provider>
  );
}
